import type {
  GreatKingdomAction,
  GreatKingdomState,
  PassAction,
  PlaceCastleAction,
  PlaceNeutralAction,
  ValidationResult,
} from "../types";
import {
  getCell,
  isCellInOpponentTerritory,
  isInBounds,
  isPlayerId,
} from "../utils";

const ok: ValidationResult = { valid: true };

function fail(
  error: string,
  details?: Record<string, unknown>,
): ValidationResult {
  return { valid: false, error, details };
}

function validateActor(
  state: GreatKingdomState,
  playerId: string,
): ValidationResult {
  if (!isPlayerId(playerId)) {
    return fail("Unknown player", { playerId });
  }
  if (state.phase === "game-over") {
    return fail("Game is already over");
  }
  if (state.currentPlayerId !== playerId) {
    return fail("It is not this player's turn", {
      playerId,
      currentPlayerId: state.currentPlayerId,
    });
  }
  return ok;
}

function validatePosition(row: number, col: number): ValidationResult {
  if (!isInBounds(row, col)) {
    return fail("Position is outside the board", { row, col });
  }
  return ok;
}

export function validatePlaceNeutral(
  state: GreatKingdomState,
  action: PlaceNeutralAction,
): ValidationResult {
  const actor = validateActor(state, action.playerId);
  if (!actor.valid) return actor;
  if (state.phase !== "neutral-setup") {
    return fail("Neutral castle can only be placed during setup");
  }
  const position = validatePosition(action.row, action.col);
  if (!position.valid) return position;
  if (getCell(state.board, action.row, action.col) !== "empty") {
    return fail("Position is already occupied", {
      row: action.row,
      col: action.col,
    });
  }
  return ok;
}

export function validatePlaceCastle(
  state: GreatKingdomState,
  action: PlaceCastleAction,
): ValidationResult {
  const actor = validateActor(state, action.playerId);
  if (!actor.valid) return actor;
  if (state.phase !== "playing") {
    return fail("Castle can only be placed during play");
  }
  const position = validatePosition(action.row, action.col);
  if (!position.valid) return position;
  if (getCell(state.board, action.row, action.col) !== "empty") {
    return fail("Position is already occupied", {
      row: action.row,
      col: action.col,
    });
  }
  if (
    isCellInOpponentTerritory(
      state.board,
      action.row,
      action.col,
      action.playerId,
    )
  ) {
    return fail("Cannot place inside completed opponent territory", {
      row: action.row,
      col: action.col,
      playerId: action.playerId,
    });
  }
  return ok;
}

export function validatePass(
  state: GreatKingdomState,
  action: PassAction,
): ValidationResult {
  const actor = validateActor(state, action.playerId);
  if (!actor.valid) return actor;
  if (state.phase !== "playing") {
    return fail("Pass can only be used during play");
  }
  return ok;
}

export function validateAction(
  state: GreatKingdomState,
  action: GreatKingdomAction,
): ValidationResult {
  switch (action.type) {
    case "PlaceNeutral":
      return validatePlaceNeutral(state, action);
    case "PlaceCastle":
      return validatePlaceCastle(state, action);
    case "Pass":
      return validatePass(state, action);
  }
}
